import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, LogOut, RefreshCw, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/site";
import Seo from "@/components/site/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

const TOKEN_KEY = "wca_admin_token";
const STATUSES = ["pending", "approved", "rejected", "archived"];

function authHeader() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

function StatusPill({ status }) {
  const tone = {
    pending: "bg-clay-100 text-clay-800 border-clay-300",
    approved: "bg-sage-100 text-sage-900 border-sage-400",
    rejected: "bg-rose-100 text-rose-800 border-rose-300",
    archived: "bg-forest-100 text-forest-800 border-forest-300",
  }[status] || "bg-forest-100 text-forest-800 border-forest-300";
  return (
    <span
      className={`inline-flex items-center text-[10px] font-semibold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full border ${tone}`}
      data-testid={`listing-status-${status}`}
    >
      {status}
    </span>
  );
}

function LoginCard({ onLogin }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back");
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="glass-panel rounded-2xl p-10 w-full max-w-md"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-water-500/15 border border-water-300">
            <ShieldCheck size={18} className="text-water-700" />
          </span>
          <div>
            <p className="overline">Admin</p>
            <h1 className="font-serif text-2xl text-forest-900 tracking-tight">
              Listings dashboard
            </h1>
          </div>
        </div>
        <p className="text-sm text-forest-700 leading-relaxed mb-6">
          Enter the admin password to review provider listing submissions.
        </p>
        <div className="space-y-1.5 mb-5">
          <Label htmlFor="admin-pass">Password</Label>
          <Input
            id="admin-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="admin-password-input"
            autoFocus
          />
        </div>
        <button
          type="submit"
          className="btn-primary w-full justify-center"
          disabled={busy || !password}
          data-testid="admin-login-submit"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {busy ? "Signing in" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function ListingRow({ listing, onUpdate }) {
  const [busy, setBusy] = useState(false);

  const setStatus = async (next) => {
    if (next === listing.status) return;
    setBusy(true);
    try {
      await api.patch(
        `/admin/listings/${listing.id}`,
        { status: next },
        { headers: authHeader() },
      );
      toast.success(`Marked as ${next}`);
      onUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="glass-panel rounded-2xl p-6 sm:p-7 transition-shadow hover:shadow-lift"
      data-testid={`listing-row-${listing.id}`}
    >
      <header className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <StatusPill status={listing.status || "pending"} />
            <span className="text-xs text-forest-600 font-sans">
              {fmt(listing.created_at)}
            </span>
          </div>
          <h3 className="font-serif text-xl text-forest-900 tracking-tight">
            {listing.business_name}
          </h3>
          <p className="text-sm text-forest-700 mt-0.5">
            {listing.contact_name} ·{" "}
            <a
              className="underline decoration-water-400/60 underline-offset-2 hover:text-water-700"
              href={`mailto:${listing.email}`}
            >
              {listing.email}
            </a>
            {listing.phone ? ` · ${listing.phone}` : ""}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-forest-600">
            {listing.state}
          </p>
          {listing.location && (
            <p className="text-sm text-forest-700">{listing.location}</p>
          )}
        </div>
      </header>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm">
        {listing.website && (
          <div>
            <dt className="text-forest-600 text-xs uppercase tracking-wider">Website</dt>
            <dd>
              <a
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-water-700 hover:text-water-800 break-all"
              >
                {listing.website}
              </a>
            </dd>
          </div>
        )}
        {listing.services && (
          <div>
            <dt className="text-forest-600 text-xs uppercase tracking-wider">Services</dt>
            <dd className="text-forest-800">{listing.services}</dd>
          </div>
        )}
      </dl>

      {listing.message && (
        <p className="mt-4 text-sm text-forest-800 leading-relaxed bg-water-50/60 border border-water-200/60 rounded-xl p-4">
          {listing.message}
        </p>
      )}

      <footer className="mt-5 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            disabled={busy || s === listing.status}
            className={`text-xs font-semibold tracking-wider uppercase px-3 py-1.5 rounded-full border transition ${
              s === listing.status
                ? "bg-forest-900 text-white border-forest-900 cursor-default"
                : "bg-white/70 text-forest-800 border-forest-300 hover:bg-water-100"
            }`}
            data-testid={`listing-action-${s}-${listing.id}`}
          >
            {s}
          </button>
        ))}
      </footer>
    </article>
  );
}

export default function AdminListings() {
  const [authed, setAuthed] = useState(
    () => !!localStorage.getItem(TOKEN_KEY),
  );
  const [listings, setListings] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(false);

  const fetchListings = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/listings", {
        headers: authHeader(),
      });
      setListings(data.listings || []);
      setCounts(data.counts || {});
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        toast.error("Session expired — please sign in again");
      } else {
        toast.error("Could not load listings");
      }
    } finally {
      setLoading(false);
    }
  }, [authed]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setListings([]);
  };

  const pingIndexNow = async () => {
    try {
      const { data } = await api.post(
        "/admin/indexnow",
        {},
        { headers: authHeader() },
      );
      if (data.ok) {
        toast.success(
          `IndexNow pinged · ${data.submitted} URLs submitted (HTTP ${data.status})`,
        );
      } else {
        toast.error(`IndexNow returned HTTP ${data.status}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "IndexNow ping failed");
    }
  };

  const visible = useMemo(
    () => listings.filter((l) => (l.status || "pending") === tab),
    [listings, tab],
  );

  if (!authed) {
    return (
      <>
        <Seo
          title="Admin · Pet Water Cremation Australia"
          description="Internal administrator dashboard."
          path="/admin/listings"
          noindex
        />
        <LoginCard onLogin={() => setAuthed(true)} />
      </>
    );
  }

  return (
    <div className="min-h-screen">
      <Seo
        title="Listings · Admin"
        description="Internal administrator dashboard for provider listings."
        path="/admin/listings"
        noindex
      />
      <section className="section pt-32 pb-10">
        <div className="container-editorial">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="overline">Admin</p>
              <h1 className="font-serif text-4xl sm:text-5xl text-forest-900 tracking-tight">
                Provider listings
              </h1>
              <p className="text-forest-700 mt-2 max-w-xl leading-relaxed">
                Submissions from the public &ldquo;Get listed&rdquo; form. Review,
                approve, archive or reject below.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={pingIndexNow}
                className="btn-secondary"
                data-testid="admin-indexnow"
                title="Notify Bing / Yandex that content has been updated"
              >
                <Send size={14} /> Ping IndexNow
              </button>
              <button
                type="button"
                onClick={fetchListings}
                className="btn-secondary"
                disabled={loading}
                data-testid="admin-refresh"
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
              <button
                type="button"
                onClick={logout}
                className="btn-secondary"
                data-testid="admin-logout"
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-white/60 border border-water-200/60 backdrop-blur-sm rounded-full p-1">
              {STATUSES.map((s) => (
                <TabsTrigger
                  key={s}
                  value={s}
                  className="rounded-full px-4 py-1.5 text-xs uppercase tracking-wider data-[state=active]:bg-forest-900 data-[state=active]:text-white"
                  data-testid={`admin-tab-${s}`}
                >
                  {s}{" "}
                  <span className="ml-2 text-[10px] opacity-70">
                    {counts[s] ?? 0}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            {STATUSES.map((s) => (
              <TabsContent key={s} value={s} className="mt-8">
                {loading ? (
                  <div className="flex justify-center py-16 text-forest-600">
                    <Loader2 className="animate-spin" />
                  </div>
                ) : visible.length === 0 ? (
                  <div
                    className="glass-panel rounded-2xl p-10 text-center text-forest-700"
                    data-testid="admin-empty"
                  >
                    No {s} listings.
                  </div>
                ) : (
                  <div className="space-y-5">
                    {visible.map((l) => (
                      <ListingRow
                        key={l.id}
                        listing={l}
                        onUpdate={fetchListings}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </div>
  );
}
